/**
 * Screen-space nearest picking (decision D8): project every pickable, take the nearest within its projected radius
 * plus 22 px (a 44 px touch target). Robust for tiny sprites and fingers, unlike raycasting.
 */
import * as THREE from 'three';

export interface Pickable {
  id: string;
  /** World position (relative to the floating origin). */
  position: THREE.Vector3;
  /** Projected radius in px (0 for point-like objects). */
  radiusPx: number;
  /** Ties are broken in favour of higher priority (spacecraft over bodies). */
  priority: number;
  /** Spacecraft: the bodies whose bubbles they are in (a direct hit on one of these does not beat them). */
  inside?: readonly string[];
}

export const PICK_RADIUS_PX = 22;
/**
 * A tap within this many px of a body's own disc (or marker) is a direct hit: the body wins over spacecraft nearby on
 * screen (S3-12: in the system view of 1979 a tap on Jupiter focuses Jupiter, not Voyager 1 or 2 next to it).
 */
export const DIRECT_HIT_PX = 6;

const v = new THREE.Vector3();

export function pick(
  pickables: readonly Pickable[],
  camera: THREE.Camera,
  x: number,
  y: number,
  width: number,
  height: number,
): string | null {
  let best: { id: string; score: number; inside?: readonly string[] | undefined } | null = null;
  let direct: { id: string; d: number } | null = null;
  for (const p of pickables) {
    v.copy(p.position).project(camera);
    if (v.z > 1 || v.z < -1) continue;
    const sx = ((v.x + 1) / 2) * width;
    const sy = ((1 - v.y) / 2) * height;
    const d = Math.hypot(sx - x, sy - y);
    const reach = p.radiusPx + PICK_RADIUS_PX;
    if (d > reach) continue;
    // distance outside the object's own disc, with a small bonus for priority
    const score = Math.max(0, d - p.radiusPx) - p.priority * 4;
    if (!best || score < best.score) best = { id: p.id, score, inside: p.inside };
    // (planets only: moons share their planet's bubble with the spacecraft passing them)
    if (p.priority === 0 && p.radiusPx > 0 && d <= p.radiusPx + DIRECT_HIT_PX && (!direct || d < direct.d)) {
      direct = { id: p.id, d };
    }
  }
  // a direct hit on a body beats a spacecraft that is merely nearby on screen, not one inside that body's bubble
  if (direct && best && best.id !== direct.id && best.inside && !best.inside.includes(direct.id)) return direct.id;
  return best?.id ?? null;
}
